import { Component, Injector, OnInit, ViewEncapsulation, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AbstractComponent, CacheKeyEnum, Channel, ChannelService, ConfirmationComponent, Department, DepartmentService, Pager, ResetPasswordComponent, RoleUserEnum, Team, TeamService, User, UserService } from 'lib-trend-core';
import { Observable, Subject, debounceTime, distinctUntilChanged, forkJoin, map, switchMap, takeUntil } from 'rxjs';
import { Constants } from '../../../environments/constantes';
import { UserUploadComponent } from './user-upload/user-upload.component';

@Component({
    selector: 'user-component',
    // templateUrl: 'user.component.html',
    templateUrl: 'user.v2.component.html',
    styleUrls: ['./user.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class UserComponent extends AbstractComponent implements OnInit {
  private selectedPermission: string | null = null;
  private selectedDepartmentId: string | null = null;

  pager: Pager<User> = new Pager<User>({ perPage: 10 });
  listObservable: Observable<Pager<User>>;
  private termOfSearch: Subject<string> = new Subject<string>();

  defaultValue: string = '';

  user: User;
  teams: Array<Team> = new Array<Team>();

  departments: Array<Department> = new Array<Department>();

  // a removed channel does not remove its departments/teams, so teams must
  // be cross-checked against the active channels to avoid showing stale departments
  private activeChannelIds: Set<string> = new Set<string>();

  roleAdmin: RoleUserEnum = RoleUserEnum.ADMIN;
  roleSupervisor: RoleUserEnum = RoleUserEnum.SUPERVISOR;
  roleAttendant: RoleUserEnum = RoleUserEnum.ATTENDANT;

  labelRoles: string;
  labelDepartment: string;

  totalUsers: number = 0;
  maxUsers: number = 0;

  constructor(
    public injector: Injector,
    public dialog: MatDialog,
    public userService: UserService,
    private teamService: TeamService,
    private departmentService: DepartmentService,
    private channelService: ChannelService,
  ) {
    super(injector);
    this.setupForm();
    this.loadingContent = true;
    this.cacheKey = CacheKeyEnum.USER_LIST;
  }

  ngOnInit() {
    this.totalUsers = +localStorage.getItem(Constants.currentTotalUsers);
    this.maxUsers = +localStorage.getItem(Constants.currentMaxUsersCompany);
  }

  ngAfterViewInit(): void {
    forkJoin({
      teams: this.teamService.getList(),
      departments: this.departmentService.getList(),
      channels: this.channelService.getList(),
    }).pipe(
      takeUntil(this.destroy$),
      map(({ teams, departments, channels }) => {
        this.activeChannelIds = new Set(channels.map((channel: Channel) => channel._id));
        this.teams = teams.filter((team: Team) => this.isTeamFromActiveChannel(team));
        this.departments = departments.filter((department: Department) => this.isDepartmentFromActiveChannel(department));
        return this.restoreState<User>();
      }),
    ).subscribe({
      next: (cached) => {
        if (!cached) {
          this.getList();
          return;
        }
        this.pager = cached.pager;
        this.searchString = cached.search ?? '';
        this.searchParams = cached.params ?? {};
        this.loadingContent = false;

        if (this.searchString) {
          this.formGroup.get('search').setValue(this.searchString);
        }

        this.selectedPermission = this.searchParams['roles'] ?? null;
        this.labelRoles = this.resolveLabelRole(this.selectedPermission);

        this.selectedDepartmentId = this.searchParams['department'] ?? null;
        this.labelDepartment = this.resolveLabelDepartment(this.selectedDepartmentId);
      },
      error: (err) => this.alertService.error(err.error.message),
    });
  }

  private resolveLabelRole(permission: string | null): string {
    switch (permission) {
      case this.roleAdmin: return this.t('user.home.roleAdmin');
      case this.roleSupervisor: return this.t('user.home.roleSupervisor');
      case this.roleAttendant: return this.t('user.home.roleAttendant');
      default: return this.t('user.home.permissions');
    }
  }

  private resolveLabelDepartment(departmentId: string | null): string {
    if (!departmentId || !this.departments.length) return this.t('user.home.departments');
    return this.departments.find(d => d._id === departmentId)?.name ?? this.t('user.home.departments');
  }

  private setupForm(): void {
    this.formGroup = this.formBuilder.group({
      search: [''],
    });
  }

  /***/
  getList(permission?: string, departmentId?: string) {
    this.loadingContent = true;
    this.searchParams = {
      company: this.getIDCurrentCompany(),
    };

    if (permission) {
      this.searchParams['roles'] = permission;
    }

    if (departmentId) {
      this.searchParams['department'] = departmentId;
    }

    this.userService.getAll(this.pager.page, this.pager.perPage, this.searchString, this.searchParams)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (pager: Pager<User>) => {
          this.pager = pager;
          this.setupObservableSearch();
          this.persistListState(this.pager, this.searchString, this.searchParams);
        },
        complete: () => this.loadingContent = false,
        error: (err) => this.alertService.error(err.error.message)
      });
  }

  getListTeam() {
    this.teamService.getList()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (list: Array<Team>) => {
          this.teams = list.filter((team: Team) => this.isTeamFromActiveChannel(team));
        },
        error: (err) => this.alertService.error(err.error.message)
      });
  }

  private isTeamFromActiveChannel(team: Team): boolean {
    if (!team.department || team.department.removed) return false;
    return this.isDepartmentFromActiveChannel(team.department);
  }

  private isDepartmentFromActiveChannel(department: Department): boolean {
    // channel comes unpopulated (plain id) from /team/list and /department/list
    const channelId = String((department.channel as any)?._id ?? department.channel);
    return this.activeChannelIds.has(channelId);
  }

  getDepartmentName(user: User): { display: string; tooltip: string } {
    const relevantDepartments = this.teams
      .filter((team: Team) => {
        const isAgent = team.agents.some((agent: User) => agent._id === user._id);
        const isSupervisor = team.supervisors.some((supervisor: User) => supervisor._id === user._id);
        return isAgent || isSupervisor;
      })
      .filter(team => team.department && !team.department.removed)
      .map(team => team.department.name);

    const uniqueDepartmentNames = Array.from(new Set(relevantDepartments));

    if (uniqueDepartmentNames.length === 0) {
      return { display: '-', tooltip: '' };
    }

    const firstDepartment = uniqueDepartmentNames[0];
    const remainingCount = uniqueDepartmentNames.length - 1;
    const tooltipContent = uniqueDepartmentNames.slice(0, 4).join(', ');

    return {
      display: remainingCount > 0 ? `${firstDepartment} +${remainingCount}` : firstDepartment,
      tooltip: tooltipContent
    };
  }

  getDaysWeek(user: User): string {
    const departmentDaysMap = new Map<string, Set<string>>();

    this.teams
      .filter((team: Team) => {
        const isAgent = team.agents.some((agent: User) => agent._id === user._id);
        const isSupervisor = team.supervisors.some((supervisor: User) => supervisor._id === user._id);
        return (isAgent || isSupervisor) && team.department && !team.department.removed;
      })
      .forEach(team => {
        const deptName = team.department.name;
        if (!departmentDaysMap.has(deptName)) {
          departmentDaysMap.set(deptName, new Set<string>());
        }

        const daysSet = departmentDaysMap.get(deptName)!;
        const schedules = team.department.schedules;
        if (schedules?.monday?.actived) daysSet.add('Seg');
        if (schedules?.tuesday?.actived) daysSet.add('Ter');
        if (schedules?.wednesday?.actived) daysSet.add('Qua');
        if (schedules?.thursday?.actived) daysSet.add('Qui');
        if (schedules?.friday?.actived) daysSet.add('Sex');
        if (schedules?.saturday?.actived) daysSet.add('Sáb');
        if (schedules?.sunday?.actived) daysSet.add('Dom');
      });


    if (departmentDaysMap.size === 0) {
      return '-';
    }

    if (departmentDaysMap.size === 1) {
      const daysSet = Array.from(departmentDaysMap.values())[0];
      const orderedDays = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'];
      const resultDays = this.joinWeekdaysShort(orderedDays.filter(day => daysSet.has(day)));

      if (resultDays === '') {
        const hours = this.getHours(user);
        if (hours === '00:00 - 23:59') {
          return this.joinWeekdaysShort(orderedDays);
        }
      }

      return resultDays || '-';
    }

    const firstDeptDays = Array.from(departmentDaysMap.values())[0];
    const orderedDays = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'];
    const resultDays = this.joinWeekdaysShort(orderedDays.filter(day => firstDeptDays.has(day)));

    if (resultDays === '') {
      const hours = this.getHours(user);
      if (hours === '00:00 - 23:59') {
        return this.joinWeekdaysShort(orderedDays);
      }
    }

    return resultDays || '-';
  }

  private joinWeekdaysShort(days: string[]): string {
    const weekdayShortKeys: Record<string, string> = {
      'Seg': 'user.weekdayShort.mon',
      'Ter': 'user.weekdayShort.tue',
      'Qua': 'user.weekdayShort.wed',
      'Qui': 'user.weekdayShort.thu',
      'Sex': 'user.weekdayShort.fri',
      'Sáb': 'user.weekdayShort.sat',
      'Dom': 'user.weekdayShort.sun',
    };
    return days.map(day => this.t(weekdayShortKeys[day] ?? day)).join(' - ');
  }


  getHours(user: User): string {
    const departmentHoursMap = new Map<string, { startHour: string, endHour: string }>();

    this.teams
      .filter((team: Team) => {
        const isAgent = team.agents.some((agent: User) => agent._id === user._id);
        const isSupervisor = team.supervisors.some((supervisor: User) => supervisor._id === user._id);
        return (isAgent || isSupervisor) && team.department && !team.department.removed;
      })
      .forEach(team => {
        const deptName = team.department.name;
        if (!departmentHoursMap.has(deptName)) {
          const schedules = team.department.schedules;
          let startHour = '00:00';
          let endHour = '23:59';

          const activeDays = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
          for (const day of activeDays) {
            if (schedules?.[day]?.actived && schedules?.[day]?.startTime && schedules?.[day]?.endTime) {
              startHour = schedules[day].startTime.replace(/(\d{2})(\d{2})/, '$1:$2');
              endHour = schedules[day].endTime.replace(/(\d{2})(\d{2})/, '$1:$2');
              break;
            }
          }
          departmentHoursMap.set(deptName, { startHour, endHour });
        }
      });

    if (departmentHoursMap.size === 0) {
      return '-';
    }

    const firstDeptHours = Array.from(departmentHoursMap.values())[0];
    return `${firstDeptHours.startHour} - ${firstDeptHours.endHour}`;
  }


  /***/
  loadPage(page: number) {
    this.pager.page = page;
    this.getList();
  }

  /***/
  delete(user: any) {
    const dialogRef = this.dialog.open(ConfirmationComponent, {
      width: '600px',
    });
    dialogRef.afterClosed().subscribe(result => {
      if (Boolean(result) === true) {
        this.userService.delete(user._id).subscribe({
          next: (value) => {
            this.getList();
            this.alertService.success();
          },
          error: (err) => this.alertService.error(err.error.message)
        });
      }
    });
  }

  /***/
  showAddUser() {
    if (this.totalUsers >= this.maxUsers) {
      this.alertService.error(this.t('user.home.userLimitReached'));
      return;
    } else {
      this.router.navigate(['/user/new'], { relativeTo: this.route });
    }
  }

  /***/
  edit(user?: any) {
    this.router.navigate([`/user/${user._id}/edit`], { relativeTo: this.route });
  }

  /***/
  setupObservableSearch() {
    this.listObservable = this.termOfSearch
      .pipe(
        takeUntil(this.destroy$),
        debounceTime(500),
        distinctUntilChanged(),
        switchMap(term => {
          this.searchString = term;
          return this.userService.getAll(this.pager.page, this.pager.perPage, this.searchString, this.searchParams);
        })
      )
      .pipe();
    this.listObservable.subscribe({
      next: (pager: Pager<User>) => {
        this.pager = pager;
        this.persistListState(this.pager, this.searchString, this.searchParams);
      },
      complete: () => this.loadingContent = false,
    });
  }

  /***/
  search(term: string) {
    this.pager.page = 1;
    this.searchString = term;
    this.termOfSearch.next(term);
  }

  getRoleClass(role: string): string {
    switch (role) {
      case RoleUserEnum.ADMIN:
        return 'bg-green-200 text-green-600';
      case RoleUserEnum.SUPERVISOR:
        return 'bg-purple-200 text-purple-600';
      default:
        return 'bg-blue-200 text-blue-600 ';
    }
  }

  toggleUserBlockStatus(user: User) {
    const dialogRef = this.dialog.open(ConfirmationComponent, {
      width: '600px',
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result !== undefined) {
        const updatedUser = { ...user, blocked: !user.blocked };
        this.userService.update(updatedUser._id, updatedUser).subscribe({
          next: () => {
            this.getList();
            this.alertService.success(updatedUser.blocked ? this.t('user.home.userBlockedSuccess') : this.t('user.home.userUnblockedSuccess'));
          },
          error: (err) => this.alertService.error(err.error.message)
        });
      }
    });
  }

  resetPassword(user: User): void {
    if (user && user._id) {
      this.dialog.open(ResetPasswordComponent, {
        width: '500px',
        height: '420px',
        data: { userId: user._id }
      });
    } else {
      console.error('Usuário não encontrado ou ID não definido');
    }
  }

  verifyAdmin(): boolean {
    return this.getRoles().includes(RoleUserEnum.ADMIN) ? true : false;
  }

  private getListDepartment(): void {
    this.departmentService.getList().subscribe({
      next: (departments: Array<Department>) => {
        this.departments = departments;
      },
    });
  }

  searchGetListByPermission(permission: string): void {
    if (!permission) {
      this.selectedPermission = null;
      this.labelRoles = this.t('user.home.permissions');
    } else {
      this.selectedPermission = permission;
      this.labelRoles = permission === this.roleAdmin ?
        this.t('user.home.roleAdmin') : permission === this.roleSupervisor ?
          this.t('user.home.roleSupervisor') : permission === this.roleAttendant ?
            this.t('user.home.roleAttendant') : this.t('user.home.permissions');
    }

    this.getList(this.selectedPermission, this.selectedDepartmentId);
  }

  searchGetListByDepartment(department: Department): void {
    if (!department) {
      this.selectedDepartmentId = null;
      this.labelDepartment = this.t('user.home.departments');
    } else {
      this.selectedDepartmentId = department._id;
      this.labelDepartment = department.name;
    }

    this.getList(this.selectedPermission, this.selectedDepartmentId);
  }

  import(): void {
    const dialogRef = this.dialog.open(UserUploadComponent, {
      width: '58vw',
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) this.getList();
    });
  }

}
